import { useEffect } from 'react';

declare global {
  interface Window {
    chatwootSettings?: {
      hideMessageBubble?: boolean;
      position?: string;
      locale?: string;
      type?: string;
      darkMode?: string;
    };
    chatwootSDK?: {
      run: (config: { websiteToken: string; baseUrl: string }) => void;
    };
    $chatwoot?: {
      setUser: (
        identifier: string,
        options: {
          identifier_hash: string;
          name?: string;
          email?: string;
          phone_number?: string;
        }
      ) => void;
      setCustomAttributes: (attributes: Record<string, unknown>) => void;
      toggle: (state?: 'open' | 'close') => void;
    };
  }
}

type Props = {
  websiteToken: string;
  baseUrl: string;
  userIdentifier: string;
  userId: number;
  hmacToken: string;
  userName?: string;
  userEmail?: string;
  userPhone?: string;
  country?: string;
  timezone?: string;
};

const ChatwootWidget = ({
  websiteToken,
  baseUrl,
  userIdentifier,
  userId,
  hmacToken,
  userName,
  userEmail,
  userPhone,
  country,
  timezone,
}: Props) => {
  useEffect(() => {
    // Only proceed when hash API data is available
    if (!websiteToken || !baseUrl || !userIdentifier || !hmacToken) return undefined;

    window.chatwootSettings = {
      hideMessageBubble: false,
      position: 'right',
      locale: 'en',
      type: 'standard',
      darkMode: 'auto',
    };

    const setupUser = () => {
      if (!window.$chatwoot) return;
      const userOptions: Record<string, string> = {
        identifier_hash: hmacToken,
        avatar_url: 'https://cdn.rushowl.app/avatar-placeholder.gif',
      };
      if (userName) userOptions.name = userName;
      if (userEmail) userOptions.email = userEmail;
      if (userPhone) userOptions.phone_number = userPhone;
      window.$chatwoot.setUser(userIdentifier, userOptions as any);
      window.$chatwoot.setCustomAttributes({
        uid: userIdentifier,
        sg_user_link: `https://sg.rushtrail.app/client/user/${userId}`,
        in_user_link: `https://in.rushtrail.app/client/user/${userId}`,
        country,
        timezone,
      });
      window.$chatwoot.toggle('open');
    };

    // If $chatwoot is already available, set user immediately
    if (window.$chatwoot) {
      setupUser();
      return undefined;
    }

    // Listen for chatwoot:ready BEFORE injecting script to avoid missing the event
    window.addEventListener('chatwoot:ready', setupUser);

    // Inject script if not already present
    const existingScript = document.querySelector(
      `script[src="${baseUrl}/packs/js/sdk.js"]`
    );
    if (!existingScript) {
      const script = document.createElement('script');
      script.src = `${baseUrl}/packs/js/sdk.js`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        if (window.chatwootSDK) {
          window.chatwootSDK.run({ websiteToken, baseUrl });
        }
      };
      document.body.appendChild(script);
    }

    return () => {
      window.removeEventListener('chatwoot:ready', setupUser);
    };
  }, [websiteToken, baseUrl, userIdentifier, userId, hmacToken, userName, userEmail, userPhone, country, timezone]);

  return null;
};

export default ChatwootWidget;
